import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { Node, Connection as PeregrineConnection, RenderEvent, RenderNode as IRenderNode, NodeEvent, Port, Point } from "../peregrineFlowInterfaces";
import ReactFlow, { Node as ReactFlowNode, Background, BackgroundVariant, Connection, Controls, EdgeChange, MiniMap, ReactFlowInstance, NodeChange, useOnSelectionChange, OnConnectStartParams, Panel } from 'reactflow';
import { Chip, Tooltip } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';

import 'reactflow/dist/style.css';
import FlowNode from "../Nodes/FlowNode/FlowNode";
import RenderNode from "../Nodes/RenderNode/RenderNode";
import useStore, { CustomReactFlowNode, RFState } from '../store';
import { socket } from "../socket";
import { shallow } from 'zustand/shallow';
import logo from "./logo.png"

const selector = (state: RFState) => ({
    nodes: state.nodes,
    edges: state.edges,
    appEdges: state.appEdges,
    project: state.project,
    selectedNodes: state.selectedNodes,
    onNodesChange: state.onNodesChange,
    onEdgesChange: state.onEdgesChange,
    onConnect: state.onConnect,
    setNodes: state.setNodes,
    setEdges: state.setEdges,
    setAppEdges: state.setAppEdges,
    setSelectedNodes: state.setSelectedNodes,
    setProject: state.setProject,
    removeNode: state.removeNode,
    isRunning: state.isRunning,
});

interface ConnectionPorts {
    sourcePort: Port;
    targetPort: Port;
}


export type OnChange = () => void
export type OnContextMenu = (event: React.MouseEvent<HTMLDivElement, MouseEvent>, pos: Point) => void;
export type OnConnectionDrop = (pos: Point, screenPos: Point, node: Node, handleId: string | null, handleType: string | null) => void;
export type OnFlowReady = (projectFn: (screenPos: { x: number; y: number }) => { x: number; y: number }) => void;

type NodeEditorProps = {
    onContextMenu?: OnContextMenu;
    onChange?: OnChange;
    onConnectionDrop?: OnConnectionDrop;
    onFlowReady?: OnFlowReady;
}

const NodeSelectionChangeListener = () => {
    const setSelectedNodes = useStore((state) => state.setSelectedNodes, shallow);
    useOnSelectionChange({
        onChange: ({ nodes }) => {
            setSelectedNodes(nodes as Array<CustomReactFlowNode>);
        },
    });

    return <></>;
};

const NodeEditor = (props: NodeEditorProps) => {
    const nodeTypes = useMemo(() => ({ flow_node: FlowNode, render_node: RenderNode }), []);
    const [editorInstance, setEditorInstance] = useState<ReactFlowInstance>();
    const reactFlowWrapper = useRef<HTMLDivElement>(null);
    const [connectionStartParams, setConnectionStartParams] = useState<OnConnectStartParams | null>(null);
    const {
        nodes, edges, onNodesChange, onEdgesChange, setNodes, onConnect, setAppEdges, appEdges, setSelectedNodes,
        removeNode, project, selectedNodes, isRunning
    } = useStore(selector, shallow);

    const displayEdges = useMemo(() => {
        if (isRunning) {
            return edges.map(edge => ({
                ...edge,
                animated: true,
                style: { ...edge.style, stroke: '#ff0000', strokeWidth: 2 }
            }));
        }
        return edges;
    }, [edges, isRunning]);

    const onConnectMiddleware = useCallback(
        (connection: Connection) => {
            if (!editorInstance || isRunning)
                return

            onConnect(connection);

            if (!connection.target || !connection.source)
                return;

        }, [editorInstance, onConnect, isRunning]);

    const onConnectStart = useCallback((_: any, params: OnConnectStartParams) => {
        setConnectionStartParams(params);
    }, []);

    const onConnectEnd = useCallback(
        (event: any) => {
            const targetIsPane = (event.target as Element).classList.contains('react-flow__pane');

            if (targetIsPane && connectionStartParams && editorInstance && reactFlowWrapper.current) {
                const { top, left } = reactFlowWrapper.current.getBoundingClientRect();
                const screenPos = { x: event.clientX - left, y: event.clientY - top };
                const pos = editorInstance.project(screenPos) as Point;

                const sourceNode = nodes.find(n => n.id === connectionStartParams.nodeId);
                if (sourceNode) {
                    props.onConnectionDrop?.(pos, screenPos, sourceNode.data as unknown as Node, connectionStartParams.handleId, connectionStartParams.handleType);
                }
            }
        },
        [editorInstance, connectionStartParams, nodes, props]
    );

    const onEdgeChangeMiddleware = useCallback((changes: EdgeChange[]) => {
        if(!editorInstance) {
            return;
        }

        const edgeChanges = onEdgesChange(changes);
        return edgeChanges
    }, [editorInstance, onEdgesChange]);

    const updateRenderData = useCallback((node: IRenderNode) => {
        nodes.forEach(cachedNode => {
            const n = cachedNode as unknown as CustomReactFlowNode;
            if (n.id !== node.id || n.type !== "render_node") {
                return cachedNode;
            }

            const nodeData = n.data as IRenderNode

            if (nodeData.render_data?.callback) {
                nodeData.render_data.callback(node.render_data);
            }

        });
    }, [nodes])

    const updateNodeData = useCallback((node: IRenderNode | Node) => {
        const updatedList = nodes.map(cachedNode => {
            const n = cachedNode as unknown as CustomReactFlowNode;

            if (n.id === node.id) {
                n.data = { ...node }
                return n;
            }


            return cachedNode;
        });
        setNodes(updatedList as any);

        // Update selected nodes if they were modified
        if (selectedNodes.length > 0) {
           const updatedSelectedNodes = selectedNodes.map(selectedNode => {
                if (selectedNode.id === node.id) {
                    const updatedNode = updatedList.find(n => n.id === node.id);
                    return updatedNode as CustomReactFlowNode;
                }
                return selectedNode;
            });
            setSelectedNodes(updatedSelectedNodes);
        }

    }, [nodes, setNodes, selectedNodes, setSelectedNodes]);

    const portsFromConnection = useCallback((connection: Connection) => {
        if (!editorInstance)
            return undefined;

        if (!connection.target || !connection.source)
            return undefined;

        const target: Node = editorInstance.getNode(connection.target)!.data;
        const source: Node = editorInstance.getNode(connection.source)!.data;
        const sourcePortIdStart = connection.sourceHandle!.lastIndexOf("-")
        const targetPortIdStart = connection.targetHandle!.lastIndexOf("-")
        const sourcePortIdx = parseInt(connection.sourceHandle!.substring(sourcePortIdStart + 1))
        const targetPortIdx = parseInt(connection.targetHandle!.substring(targetPortIdStart + 1))

        if (isNaN(sourcePortIdx) || isNaN(targetPortIdx)) {
            return undefined;
        }

        if (source.output_ports.length <= sourcePortIdx || target.input_ports.length <= targetPortIdx) {
            return undefined;
        }

        return {
            sourcePort: source.output_ports[sourcePortIdx],
            targetPort: target.input_ports[targetPortIdx]
        } as ConnectionPorts

    }, [editorInstance]);

    useEffect(() => {
        if (project) {
            project.connections = appEdges;
        }

        const onRenderEvent = (render_event: RenderEvent) => {
            updateRenderData(render_event.node);
        }

        const onNodeDirty = (node_event: NodeEvent) => {
            if (node_event.event_type !== 'update')
                return

            updateNodeData(node_event.node);
        }

        socket.on('render', onRenderEvent)
        socket.on('node', onNodeDirty);

        return () => {
            socket.off('render', onRenderEvent);
            socket.off('node', onNodeDirty);
        };
    }, [updateNodeData, updateRenderData, appEdges, project])


    const isValidConnection = useCallback((connection: Connection, with_handle_check: Boolean = true) => {
        const ports = portsFromConnection(connection);

        if (!ports)
            return false;

        // Check if the target handle is already connected
        // We allow only one connection per input port
        const targetConnectable = !edges.some(e =>
            e.target === connection.target && e.targetHandle === connection.targetHandle
        );


        // an input port can only have one connection
        if (!targetConnectable && with_handle_check)
            return false;

        let lhs = ports.sourcePort.data_type;
        let rhs = ports.targetPort.data_type;

        if (!(lhs instanceof Array)) {
            lhs = [lhs]
        }

        if (!(rhs instanceof Array)) {
            rhs = [rhs]
        }

        // Return true if either side is of type Any
        if (rhs.includes('Any') || lhs.includes('Any')) {
            return true;
        }

        return lhs.some(r => rhs.includes(r))
    }, [portsFromConnection, edges]);

    useEffect(() => {
        if (!editorInstance)
            return

        let connections: Array<PeregrineConnection> = [];
        edges.forEach((connection) => {
            const targetFlowNode = editorInstance.getNode(connection.target)!;
            const sourceFlowNode = editorInstance.getNode(connection.source)!;

            const target: Node = targetFlowNode.data;
            const source: Node = sourceFlowNode.data;
            const con = connection as Connection;
            const ports = portsFromConnection(con);

            target.position = targetFlowNode.position
            source.position = sourceFlowNode.position

            if(!isValidConnection(con, false)) {
                console.warn("Invalid connection", con);
                return;
            }

            connections.push(
                {
                    provider_node: source,
                    provider_port: ports?.sourcePort.label,
                    consumer_node: target,
                    consumer_port: ports?.targetPort.label
                } as PeregrineConnection
            );
        });
        setAppEdges(connections)
        project!.connections = connections;
    }, [edges, editorInstance, portsFromConnection, setAppEdges, project, isValidConnection]);

    const onContextMenu = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
        if (!editorInstance) {
            return
        }

        const pos = editorInstance.project({
            x: e.clientX,
            // 70 is a compensation factor since the node height is unknown for here
            y: e.clientY - 70,
        }) as Point;

        e.preventDefault();
        props.onContextMenu?.(e, pos);

    }, [props, editorInstance]);


    const onInitReactFlow = useCallback((flowInstance: ReactFlowInstance) => {
        setEditorInstance(flowInstance);
        props.onFlowReady?.(flowInstance.project);
    }, [props]);

    const onNodesChangeMiddleware = useCallback((changes: Array<NodeChange>) => {
        if(!editorInstance) {
            return;
        }

        let hasChanges = false;

        changes.forEach((value) => {
            if (value.type === 'remove') {
                if (isRunning) return;  // block node deletion while engine is running

                const node = editorInstance.getNode(value.id) as ReactFlowNode<CustomReactFlowNode>;

                if (node) {
                    // Emit port_disconnect events for all edges connected to this node
                    // BEFORE the node is removed (to ensure we have access to node data)
                    const connectedEdges = edges.filter(e => e.source === node.id || e.target === node.id);
                    connectedEdges.forEach(edge => {
                        const sourceNode = nodes.find(n => n.id === edge.source);
                        const targetNode = nodes.find(n => n.id === edge.target);

                        if (sourceNode && targetNode && edge.sourceHandle && edge.targetHandle) {
                            socket.emit('port_disconnect', {
                                event_type: 'disconnect',
                                provider_node: sourceNode.data as unknown as Node,
                                provider_port: edge.sourceHandle,
                                consumer_node: targetNode.data as unknown as Node,
                                consumer_port: edge.targetHandle,
                            });
                        }
                    });

                    removeNode(node);

                    const event = { event_type: "delete", node: node.data } as unknown as NodeEvent
                    socket.emit("node", event);
                    hasChanges = true;
                }
            }
        })

        onNodesChange(changes);

        if (hasChanges) {
            setSelectedNodes([])
            props.onChange?.()
        }
    }, [editorInstance, setSelectedNodes, removeNode, onNodesChange, props, edges, nodes, isRunning]);



    const initEditor = () => {
        return (
            <div style={{ width: '100%', height: '100%' }} ref={reactFlowWrapper}>
                <ReactFlow
                    nodes={nodes} nodeTypes={nodeTypes} edges={displayEdges} minZoom={0.1} snapToGrid={true}
                    onNodesChange={onNodesChangeMiddleware} onEdgesChange={onEdgeChangeMiddleware}
                    onConnect={onConnectMiddleware} onInit={onInitReactFlow}
                    onConnectStart={onConnectStart} onConnectEnd={onConnectEnd}
                    isValidConnection={isValidConnection} onContextMenu={onContextMenu}
                    nodesConnectable={!isRunning}
                    deleteKeyCode={isRunning ? null : ["Backspace", "Delete"]}
                    onEdgesDelete={isRunning ? undefined : () => props.onChange?.()}
                    onNodesDelete={isRunning ? undefined : () => props.onChange?.()}
                    style={{ backgroundImage: `url(${logo})`, backgroundPosition: "50% 100%", backgroundSize: "auto 90%", backgroundRepeat: "no-repeat" }} >
                    <Controls />
                    <MiniMap />
                    <Background variant={BackgroundVariant.Dots} gap={25} size={2} color="#B5E2FA" />
                    <NodeSelectionChangeListener />
                    {isRunning && (
                        <Panel position="bottom-center">
                            <Tooltip title="Connections cannot be changed while the engine is running." placement="bottom">
                                <Chip
                                    icon={<LockIcon fontSize="small" />}
                                    label="Engine running – connections locked"
                                    size="small"
                                    sx={{
                                        backgroundColor: 'rgba(30,30,30,0.82)',
                                        color: '#fff',
                                        fontWeight: 500,
                                        backdropFilter: 'blur(4px)',
                                        '& .MuiChip-icon': { color: '#ffca28' },
                                        cursor: 'default',
                                        userSelect: 'none',
                                    }}
                                />
                            </Tooltip>
                        </Panel>
                    )}
                </ReactFlow>
            </div >
        );
    }

    return initEditor();
}


export default NodeEditor;
